/**
 * Safely retrieves the value of a CSS custom property (CSS variable) from the document root.
 *
 * @param property - The CSS custom property name (e.g., '--ls-color-bg-neutral-top')
 * @returns The computed value of the CSS variable, or null if:
 *   - The property is not defined
 *   - The value is empty or whitespace
 *   - The value is a CSS keyword ('initial', 'inherit', 'unset')
 *   - An error occurs during retrieval
 *
 * @example
 * ```ts
 * const bgColor = getCssVariableValue('--ls-color-bg-neutral-top')
 * if (bgColor) {
 *   // Use the color value
 * }
 * ```
 */
export const getCssVariableValue = (property: string): string | null => {
  try {
    const value = getComputedStyle(document.documentElement).getPropertyValue(
      property
    )
    // Return the value only if it's not empty, not just whitespace, and not 'initial' or 'inherit'
    const trimmedValue = value?.trim()
    if (
      trimmedValue &&
      trimmedValue !== 'initial' &&
      trimmedValue !== 'inherit' &&
      trimmedValue !== 'unset'
    ) {
      return trimmedValue
    }
  } catch {
    // Silently handle errors - CSS variables may not be available during initial load
  }
  return null
}
